import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Card, CardHeader, Input, Select, Field, Button, Table, THead, TBody, TR, TH, TD,
  StatusBadge, EmptyState, ErrorNotice, Loading, Pagination,
} from '../../components/ui/index.js';
import { PAYMENT_STATUS_OPTIONS } from '../../components/PaymentsTable.jsx';
import { useResource } from '../../hooks/useApi.js';
import { formatMoney, formatTimestamp } from '../../lib/format.js';
import { CreditCard } from 'lucide-react';
import { PageHero } from '../../components/PageHero.jsx';

const NO_FILTERS = { search: '', merchantId: '', method: '', status: '', days: 30 };

/** The method select's value is `kind:key`, so one control can ask for a gateway or an offline method. */
function methodParams(method) {
  if (!method) return {};
  const [kind, key] = method.split(':');
  return kind === 'offline' ? { offlineMethod: key } : { provider: key };
}

/**
 * Every payment on the platform, across every organisation.
 *
 * Still no customer email or card on this list: an operator reading across
 * organisations is looking for shape — an outage, a spike, an organisation
 * that has gone quiet — and none of that needs somebody's contact details.
 * Opening one payment shows what was sent to its gateway, for the times a
 * gateway asks.
 */
export function AdminPaymentsPage() {
  // Seeded from the URL, so an organisation's page can link straight to its payments.
  const [params] = useSearchParams();
  const [filters, setFilters] = useState(() => ({ ...NO_FILTERS, merchantId: params.get('merchantId') ?? '' }));
  const [search, setSearch] = useState('');
  const [skip, setSkip] = useState(0);

  // Debounced so typing does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => { setSearch(filters.search.trim()); setSkip(0); }, 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const options = useResource('/admin/payments/filters');
  const { data, error, loading, refetch } = useResource('/admin/payments', {
    params: {
      limit: 25, skip, days: filters.days,
      ...(search ? { search } : {}),
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.merchantId ? { merchantId: filters.merchantId } : {}),
      ...methodParams(filters.method),
    },
  });

  const set = (key) => (e) => { setFilters((f) => ({ ...f, [key]: e.target.value })); setSkip(0); };
  const filtered = Boolean(filters.search || filters.merchantId || filters.method || filters.status);
  const gateways = options.data?.methods?.filter((m) => m.kind === 'provider') ?? [];
  const offline = options.data?.methods?.filter((m) => m.kind === 'offline') ?? [];

  return (
    <>
      <PageHero
        chip="Platform"
        icon={CreditCard}
        title="Payments"
        blurb="Every payment taken on the platform, whoever took it. Money moves between the organisation and their own gateway — none of it passes through us."
      />

      <Card>
        <CardHeader
          title="All payments"
          blurb={data ? `${data.total} in the last ${filters.days} days` : undefined}
          actions={filtered && (
            <Button size="sm" variant="ghost" onClick={() => { setFilters(NO_FILTERS); setSkip(0); }}>Clear</Button>
          )}
        />

        <div className="grid gap-3 border-b border-line p-5 sm:grid-cols-2 xl:grid-cols-5">
          <Field label="Search">
            {(props) => (
              <Input {...props} type="search" value={filters.search} onChange={set('search')}
                placeholder="Payment ID, order, email, phone" />
            )}
          </Field>
          <Field label="Organisation">
            {(props) => (
              <Select {...props} value={filters.merchantId} onChange={set('merchantId')}>
                <option value="">All organisations</option>
                {options.data?.merchants?.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </Select>
            )}
          </Field>
          <Field label="Method">
            {(props) => (
              <Select {...props} value={filters.method} onChange={set('method')}>
                <option value="">Any method</option>
                {gateways.length > 0 && (
                  <optgroup label="Through a gateway">
                    {gateways.map((m) => <option key={m.key} value={`provider:${m.key}`}>{m.label}</option>)}
                  </optgroup>
                )}
                {offline.length > 0 && (
                  <optgroup label="Collected by the merchant">
                    {offline.map((m) => <option key={m.key} value={`offline:${m.key}`}>{m.label}</option>)}
                  </optgroup>
                )}
              </Select>
            )}
          </Field>
          <Field label="Status">
            {(props) => (
              <Select {...props} value={filters.status} onChange={set('status')}>
                {PAYMENT_STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </Select>
            )}
          </Field>
          <Field label="Period">
            {(props) => (
              <Select {...props} value={filters.days}
                onChange={(e) => { setFilters((f) => ({ ...f, days: Number(e.target.value) })); setSkip(0); }}>
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
                <option value={365}>Last year</option>
              </Select>
            )}
          </Field>
        </div>

        {options.error && <ErrorNotice error={options.error} onRetry={options.refetch} title="Could not load the filter options" className="m-4" />}
        {loading && <Loading label="Loading payments…" />}
        {error && <ErrorNotice error={error} onRetry={refetch} className="m-4" />}
        {!loading && !error && data?.payments?.length === 0 && (
          <EmptyState
            title="No payments match"
            blurb="Try a wider period, or clear the filters."
            action={filtered ? <Button variant="secondary" onClick={() => { setFilters(NO_FILTERS); setSkip(0); }}>Clear filters</Button> : undefined}
          />
        )}

        {!loading && !error && data?.payments?.length > 0 && (
          <>
            <Table>
              <THead>
                <TR>
                  <TH>Payment</TH>
                  <TH>Organisation</TH>
                  <TH align="right">Amount</TH>
                  <TH>Status</TH>
                  <TH>Method</TH>
                  <TH>Order</TH>
                  <TH align="right">When</TH>
                </TR>
              </THead>
              <TBody>
                {data.payments.map((payment) => (
                  <TR key={payment.id}>
                    <TD>
                      <Link to={`/admin/payments/${payment.id}`} className="font-mono text-xs font-medium text-accent-ink hover:underline">
                        {payment.id}
                      </Link>
                    </TD>
                    <TD>
                      <Link to={`/admin/merchants/${payment.merchantId}`} className="text-sm text-ink hover:underline">
                        {payment.merchantName ?? payment.merchantId}
                      </Link>
                    </TD>
                    <TD align="right" className="tabular text-sm font-medium text-ink">
                      {formatMoney(payment.amount, payment.currency)}
                    </TD>
                    <TD><StatusBadge status={payment.status} size="sm" /></TD>
                    {/* HC-2 does not apply here — this is our own operator, and which
                        gateway an organisation is on is the first thing they need
                        during an outage. */}
                    <TD className="text-sm text-ink-soft">{payment.method?.label ?? '—'}</TD>
                    <TD className="text-sm text-muted">{payment.orderId ?? '—'}</TD>
                    <TD align="right" className="text-xs text-muted">{formatTimestamp(payment.createdAt)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            <Pagination total={data.total} limit={data.limit} skip={data.skip} onChange={setSkip} />
          </>
        )}
      </Card>
    </>
  );
}

export default AdminPaymentsPage;
